import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { PipelineLoan, DateRange } from '../types';
import { groupLoansByMonth } from '../utils';
import { BreakdownTable } from './FundedSummaryReport';
import {
  buildBreakdown,
  buildNonQmSubBreakdown,
  formatCurrency,
  formatPct,
  getLoanTypeCategory,
  getPurposeCategory,
  getChannelCategory,
  LOAN_TYPE_LABELS,
  PURPOSE_LABELS,
  CHANNEL_LABELS
} from './fundedSummaryModel';
import { startOfYear } from 'date-fns';

interface MonthlyProductionReportProps {
  loans: PipelineLoan[];
  dateRange: DateRange;
  isLoading: boolean;
}

interface MonthBucket {
  key: string;
  label: string;
  volume: number;
  units: number;
  avgLoanSize: number;
  momDelta: number | null;
  momPct: number | null;
}

interface InvestorRow {
  name: string;
  volume: number;
  units: number;
  pct: number; // 0-100 scale
}

export function MonthlyProductionReport({ loans, dateRange, isLoading }: MonthlyProductionReportProps) {
  const from = useMemo(() => dateRange.from || startOfYear(new Date()), [dateRange.from]);
  const to = useMemo(() => dateRange.to || new Date(), [dateRange.to]);

  // Section A: Month-over-Month
  const monthBuckets = useMemo<MonthBucket[]>(() => {
    const raw = groupLoansByMonth(loans, from, to);
    return raw.map((bucket, idx) => {
      const volume = bucket.loans.reduce((s, l) => s + (l.trans_details_loan_amt || 0), 0);
      const units = bucket.loans.length;
      const avgLoanSize = units > 0 ? volume / units : 0;

      let momDelta: number | null = null;
      let momPct: number | null = null;

      if (idx > 0) {
        const prevBucket = raw[idx - 1];
        const prevVolume = prevBucket.loans.reduce((s, l) => s + (l.trans_details_loan_amt || 0), 0);
        momDelta = volume - prevVolume;
        momPct = prevVolume > 0 ? ((volume - prevVolume) / prevVolume) * 100 : null;
      }

      return { key: bucket.key, label: bucket.label, volume, units, avgLoanSize, momDelta, momPct };
    });
  }, [loans, from, to]);

  const totalVolume = useMemo(() => monthBuckets.reduce((s, b) => s + b.volume, 0), [monthBuckets]);
  const totalUnits = useMemo(() => monthBuckets.reduce((s, b) => s + b.units, 0), [monthBuckets]);

  // Section B: Top Investors
  const investorRows = useMemo<InvestorRow[]>(() => {
    const map = new Map<string, { volume: number; units: number }>();
    loans.forEach((loan) => {
      const inv = (loan.investor_name || '').trim() || 'Other';
      const existing = map.get(inv) || { volume: 0, units: 0 };
      existing.volume += loan.trans_details_loan_amt || 0;
      existing.units += 1;
      map.set(inv, existing);
    });

    const total = loans.reduce((s, l) => s + (l.trans_details_loan_amt || 0), 0);
    return [...map.entries()]
      .sort((a, b) => b[1].volume - a[1].volume)
      .map(([name, data]) => ({
        name,
        volume: data.volume,
        units: data.units,
        pct: total > 0 ? (data.volume / total) * 100 : 0
      }));
  }, [loans]);

  // Sections C/D/E
  const loanTypeRows = useMemo(
    () => buildBreakdown(loans, getLoanTypeCategory, [...LOAN_TYPE_LABELS]),
    [loans]
  );
  const nonQmSubtypeRows = useMemo(() => buildNonQmSubBreakdown(loans), [loans]);
  const purposeRows = useMemo(
    () => buildBreakdown(loans, getPurposeCategory, [...PURPOSE_LABELS]),
    [loans]
  );
  const channelRows = useMemo(
    () => buildBreakdown(loans, getChannelCategory, [...CHANNEL_LABELS]),
    [loans]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (loans.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No funded loans found for the selected criteria.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Section A: Month-over-Month Production */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Month-over-Month Production</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[140px]">Month</TableHead>
                <TableHead className="text-right">Volume ($)</TableHead>
                <TableHead className="text-right">Units</TableHead>
                <TableHead className="text-right">Avg Loan Size</TableHead>
                <TableHead className="text-right">MoM Change ($)</TableHead>
                <TableHead className="text-right">MoM Change (%)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {monthBuckets.map((bucket) => (
                <TableRow key={bucket.key}>
                  <TableCell className="font-medium">{bucket.label}</TableCell>
                  <TableCell className="text-right">{formatCurrency(bucket.volume)}</TableCell>
                  <TableCell className="text-right">{bucket.units}</TableCell>
                  <TableCell className="text-right">{formatCurrency(bucket.avgLoanSize)}</TableCell>
                  <TableCell
                    className={`text-right ${
                      bucket.momDelta !== null && bucket.momDelta > 0
                        ? 'text-emerald-600 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-950'
                        : bucket.momDelta !== null && bucket.momDelta < 0
                          ? 'text-rose-600 bg-rose-50 dark:text-rose-400 dark:bg-rose-950'
                          : ''
                    }`}
                  >
                    {bucket.momDelta !== null ? (
                      <span className="inline-flex items-center gap-1">
                        {bucket.momDelta > 0 && <TrendingUp className="h-3.5 w-3.5" />}
                        {bucket.momDelta < 0 && <TrendingDown className="h-3.5 w-3.5" />}
                        {formatCurrency(bucket.momDelta)}
                      </span>
                    ) : (
                      '\u2014'
                    )}
                  </TableCell>
                  <TableCell
                    className={`text-right ${
                      bucket.momPct !== null && bucket.momPct > 0
                        ? 'text-emerald-600 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-950'
                        : bucket.momPct !== null && bucket.momPct < 0
                          ? 'text-rose-600 bg-rose-50 dark:text-rose-400 dark:bg-rose-950'
                          : ''
                    }`}
                  >
                    {bucket.momPct !== null ? formatPct(bucket.momPct) : '\u2014'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell className="font-bold">TOTAL</TableCell>
                <TableCell className="text-right font-bold">{formatCurrency(totalVolume)}</TableCell>
                <TableCell className="text-right font-bold">{totalUnits}</TableCell>
                <TableCell className="text-right font-bold">
                  {formatCurrency(totalUnits > 0 ? totalVolume / totalUnits : 0)}
                </TableCell>
                <TableCell />
                <TableCell />
              </TableRow>
            </TableFooter>
          </Table>
        </CardContent>
      </Card>

      {/* Section B: Top Investors */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Top Investors</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[300px]">Investor</TableHead>
                <TableHead className="text-right">Volume ($)</TableHead>
                <TableHead className="text-right">Units</TableHead>
                <TableHead className="text-right">% of Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {investorRows.map((row) => (
                <TableRow key={row.name}>
                  <TableCell>{row.name}</TableCell>
                  <TableCell className="text-right">{formatCurrency(row.volume)}</TableCell>
                  <TableCell className="text-right">{row.units}</TableCell>
                  <TableCell className="text-right">{formatPct(row.pct)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Sections C/D: Loan Type + Purpose side-by-side */}
      <div className="grid gap-6 md:grid-cols-2">
        <BreakdownTable
          title="Volume by Loan Type"
          dimensionLabel="Loan Type"
          rows={loanTypeRows}
          subRows={{ 'Non-QM': nonQmSubtypeRows }}
        />
        <BreakdownTable title="Volume by Purpose" dimensionLabel="Purpose" rows={purposeRows} />
      </div>

      {/* Section E: Channel */}
      <BreakdownTable title="Volume by Channel" dimensionLabel="Channel" rows={channelRows} />
    </div>
  );
}
